'use client';

import React from 'react';
import { ChevronRight } from 'lucide-react';
import type { AuditLog } from '@/lib/services/staffingService';

interface AuditTableProps {
  logs: AuditLog[];
  loading: boolean;
  onRowClick: (log: AuditLog) => void;
}

const SEVERITY_STYLES: Record<string, string> = {
  info: 'bg-primary/10 text-primary',
  warning: 'bg-warning/15 text-warning',
  critical: 'bg-danger/10 text-danger',
};

const ACTION_LABELS: Record<string, string> = {
  role_change: 'Role Change',
  credential_update: 'Credential Update',
  shift_modification: 'Shift Modification',
  approval: 'Approval',
  login: 'Login',
  logout: 'Logout',
  user_created: 'User Created',
  user_deactivated: 'User Deactivated',
  invoice_created: 'Invoice Created',
  invoice_updated: 'Invoice Updated',
  timesheet_approved: 'Timesheet Approved',
  timesheet_rejected: 'Timesheet Rejected',
  job_posted: 'Job Posted',
  applicant_stage_changed: 'Applicant Stage',
  settings_updated: 'Settings Updated',
};

const ACTION_STYLES: Record<string, string> = {
  role_change: 'bg-purple-500/10 text-purple-400',
  credential_update: 'bg-blue-500/10 text-blue-400',
  shift_modification: 'bg-cyan-500/10 text-cyan-400',
  approval: 'bg-green-500/10 text-green-400',
  timesheet_approved: 'bg-green-500/10 text-green-400',
  timesheet_rejected: 'bg-red-500/10 text-red-400',
  user_created: 'bg-emerald-500/10 text-emerald-400',
  user_deactivated: 'bg-orange-500/10 text-orange-400',
  invoice_created: 'bg-indigo-500/10 text-indigo-400',
  invoice_updated: 'bg-indigo-500/10 text-indigo-400',
  job_posted: 'bg-teal-500/10 text-teal-400',
  applicant_stage_changed: 'bg-sky-500/10 text-sky-400',
  settings_updated: 'bg-amber-500/10 text-amber-400',
  login: 'bg-muted text-muted-foreground',
  logout: 'bg-muted text-muted-foreground',
};

function formatDateTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AuditTable({ logs, loading, onRowClick }: AuditTableProps) {
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="divide-y divide-border">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-5 py-3.5 animate-pulse">
              <div className="h-4 bg-muted rounded w-32" />
              <div className="h-4 bg-muted rounded w-24" />
              <div className="h-5 bg-muted rounded w-28" />
              <div className="h-5 bg-muted rounded w-20" />
              <div className="h-4 bg-muted rounded flex-1" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl flex flex-col items-center justify-center py-16 text-center">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
          <span className="text-2xl">🔍</span>
        </div>
        <p className="text-sm font-medium text-foreground">No audit events found</p>
        <p className="text-xs text-muted-foreground mt-1">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Table header */}
      <div className="grid grid-cols-[160px_140px_140px_100px_1fr_32px] gap-3 px-5 py-2.5 border-b border-border bg-muted/40">
        <span className="text-[11px] font-600 uppercase tracking-wider text-muted-foreground">Timestamp</span>
        <span className="text-[11px] font-600 uppercase tracking-wider text-muted-foreground">Performed By</span>
        <span className="text-[11px] font-600 uppercase tracking-wider text-muted-foreground">Action</span>
        <span className="text-[11px] font-600 uppercase tracking-wider text-muted-foreground">Severity</span>
        <span className="text-[11px] font-600 uppercase tracking-wider text-muted-foreground">Description</span>
        <span />
      </div>

      {/* Rows */}
      <div className="divide-y divide-border">
        {logs.map((log) => (
          <button
            key={log.id}
            onClick={() => onRowClick(log)}
            className="w-full grid grid-cols-[160px_140px_140px_100px_1fr_32px] gap-3 px-5 py-3.5 text-left hover:bg-muted/30 transition-colors group"
          >
            <span className="text-xs text-muted-foreground tabular-nums leading-5">
              {formatDateTime(log.createdAt)}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate leading-5">{log.performedByName}</p>
              <p className="text-[11px] text-muted-foreground truncate">{log.performedByRole}</p>
            </div>
            <div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-600 ${ACTION_STYLES[log.actionType] || 'bg-muted text-muted-foreground'}`}>
                {ACTION_LABELS[log.actionType] || log.actionType}
              </span>
            </div>
            <div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-600 capitalize ${SEVERITY_STYLES[log.severity] || ''}`}>
                {log.severity}
              </span>
            </div>
            <p className="text-sm text-foreground/80 truncate leading-5">{log.description}</p>
            <ChevronRight size={14} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity self-center" />
          </button>
        ))}
      </div>
    </div>
  );
}
